import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Upload, X } from "lucide-react";

const CATEGORIES = ["Policy", "Community", "Technology", "Environment", "Education", "Housing", "Health", "Economic"];

type Prefill = {
  title?: string;
  description?: string;
  category?: string;
  tags?: string[];
};

type EditIdea = {
  id: string;
  title: string;
  description: string | null;
  category: string;
  tags: string[] | null;
  image_url: string | null;
};

const NewIdeaDialog = ({
  open,
  onOpenChange,
  prefill,
  idea,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  prefill?: Prefill;
  idea?: EditIdea;
  onSaved?: () => void;
}) => {
  const isEdit = !!idea;
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [title, setTitle] = useState(idea?.title ?? prefill?.title ?? "");
  const [description, setDescription] = useState(idea?.description ?? prefill?.description ?? "");
  const [category, setCategory] = useState(idea?.category ?? prefill?.category ?? "Community");
  const [tagsInput, setTagsInput] = useState(
    (idea?.tags ?? prefill?.tags ?? []).join(", "),
  );
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(idea?.image_url ?? null);
  const [existingImageUrl, setExistingImageUrl] = useState<string | null>(idea?.image_url ?? null);

  // Re-sync when dialog opens or the target idea changes.
  useEffect(() => {
    if (!open) return;
    setTitle(idea?.title ?? prefill?.title ?? "");
    setDescription(idea?.description ?? prefill?.description ?? "");
    setCategory(idea?.category ?? prefill?.category ?? "Community");
    setTagsInput((idea?.tags ?? prefill?.tags ?? []).join(", "));
    setImageFile(null);
    setImagePreview(idea?.image_url ?? null);
    setExistingImageUrl(idea?.image_url ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, idea?.id]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const saveIdea = useMutation({
    mutationFn: async () => {
      let imageUrl: string | null = isEdit ? existingImageUrl : null;

      if (imageFile && user) {
        const ext = imageFile.name.split(".").pop();
        const path = `${user.id}/${Date.now()}.${ext}`;
        const { error: uploadError } = await supabase.storage
          .from("idea-images")
          .upload(path, imageFile);
        if (uploadError) throw uploadError;

        const { data: urlData } = supabase.storage
          .from("idea-images")
          .getPublicUrl(path);
        imageUrl = urlData.publicUrl;
      }

      const tags = tagsInput
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean);

      if (isEdit && idea) {
        const { error } = await supabase
          .from("ideas")
          .update({
            title: title.trim(),
            description: description.trim() || null,
            category,
            tags: tags.length > 0 ? tags : null,
            image_url: imageUrl,
          })
          .eq("id", idea.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("ideas").insert({
          title: title.trim(),
          description: description.trim() || null,
          category,
          tags: tags.length > 0 ? tags : null,
          image_url: imageUrl,
          user_id: user!.id,
        });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      toast({
        title: isEdit ? "Project updated" : "Idea shared!",
        description: isEdit
          ? "Your changes have been saved."
          : "Your idea is now visible to the community.",
      });
      queryClient.invalidateQueries({ queryKey: ["ideas"] });
      if (isEdit && idea) {
        queryClient.invalidateQueries({ queryKey: ["idea-detail", idea.id] });
      }
      onSaved?.();
      resetForm();
      onOpenChange(false);
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const resetForm = () => {
    if (!isEdit) {
      setTitle("");
      setDescription("");
      setCategory("Community");
      setTagsInput("");
      setImageFile(null);
      setImagePreview(null);
      setExistingImageUrl(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-foreground">
            {isEdit ? "Edit Project" : "Share an Idea"}
          </DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            saveIdea.mutate();
          }}
          className="space-y-4"
        >
          <div>
            <Label className="text-foreground">Title *</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="A bold, actionable idea" required className="mt-1" maxLength={200} />
          </div>

          <div>
            <Label className="text-foreground">Description</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Describe the idea, who it helps, and how it could work..." className="mt-1 resize-none" rows={4} maxLength={2000} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-foreground">Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-foreground">Tags</Label>
              <Input value={tagsInput} onChange={(e) => setTagsInput(e.target.value)} placeholder="housing, mobile" className="mt-1" maxLength={200} />
              <p className="text-xs text-muted-foreground mt-0.5">Comma-separated</p>
            </div>
          </div>

          {/* Image upload */}
          <div>
            <Label className="text-foreground">Cover Image</Label>
            {imagePreview ? (
              <div className="relative mt-1 rounded-lg overflow-hidden border border-border">
                <img src={imagePreview} alt="Preview" className="w-full h-40 object-cover" />
                <button
                  type="button"
                  onClick={() => {
                    setImageFile(null);
                    setImagePreview(null);
                    setExistingImageUrl(null);
                  }}
                  className="absolute top-2 right-2 bg-background/80 rounded-full p-1"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <label className="mt-1 flex items-center justify-center gap-2 border-2 border-dashed border-border rounded-lg p-6 cursor-pointer hover:border-primary/40 transition-colors">
                <Upload className="h-5 w-5 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Upload an image</span>
                <input type="file" className="hidden" accept="image/*" onChange={handleImageChange} />
              </label>
            )}
          </div>

          <Button type="submit" className="w-full" disabled={!title.trim() || saveIdea.isPending}>
            {saveIdea.isPending
              ? isEdit ? "Saving..." : "Sharing..."
              : isEdit ? "Save Changes" : "Share Idea"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default NewIdeaDialog;
